import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { notificationSettingsApi, notificationsApi } from "../notificationsApi";

export const notificationKeys = {
  all: ["notifications"],
  unread: () => ["notifications", "unread"],
  list: (params) => ["notifications", "list", params],
};

/** Unread badge: polled every 60s and refreshed when the window gets focus again. */
export const useUnreadNotifications = ({ enabled = true } = {}) =>
  useQuery({
    queryKey: notificationKeys.unread(),
    queryFn: notificationsApi.unreadCount,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: true,
    staleTime: 30 * 1000,
    enabled,
  });

export const useNotifications = (params, { enabled = true } = {}) =>
  useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: () => notificationsApi.list(params),
    enabled,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });

const useNotificationMutation = (fn) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    // The response carries the new unread count: show it at once, then refresh the list
    onSuccess: (res) => {
      if (typeof res?.unread === "number")
        qc.setQueryData(notificationKeys.unread(), { unread: res.unread });
      qc.invalidateQueries({ queryKey: ["notifications", "list"] });
    },
  });
};
export const useMarkNotificationRead = () =>
  useNotificationMutation((id) => notificationsApi.markRead(id));
export const useMarkAllNotificationsRead = () =>
  useNotificationMutation(() => notificationsApi.markAllRead());
export const useMarkNotificationsRead = () =>
  useNotificationMutation((ids) => notificationsApi.markManyRead(ids));

/* ---------- Settings (Administration → Notification Settings) ---------- */
export const useNotificationCatalog = () =>
  useQuery({
    queryKey: ["notificationSettings", "catalog"],
    queryFn: notificationSettingsApi.catalog,
    staleTime: 30 * 60 * 1000,
  });
export const useNotificationSettings = (companyId, { enabled = true } = {}) =>
  useQuery({
    queryKey: ["notificationSettings", companyId || "mine"],
    queryFn: () => notificationSettingsApi.get(companyId),
    enabled,
  });
const useSettingsMutation = (fn) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    // Sub-companies may inherit it, and the bell follows the new setting
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notificationSettings"] });
      qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
};
export const useSaveNotificationSettings = () =>
  useSettingsMutation(({ companyId, config }) =>
    notificationSettingsApi.save(companyId, config),
  );
export const useResetNotificationSettings = () =>
  useSettingsMutation((companyId) => notificationSettingsApi.reset(companyId));
